import App from "@bloglovin/inspire-engine/App";
import { PLATFORM_ENTITY_APP_NAME, isBuilderAppBooted, isCompiledRenderSurface }
  from "%/dynamic-component-editor/classes/platform-standard-entities";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import ReferencedComponentId from "%/dynamic-component-editor/options/referenced-component-id";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MinHeight from "%/dynamic-component-editor/options/min-height";

const { boundComponentIdFromStored, unboundComponentMessage }
  = require("%/dynamic-component-editor/helpers/host-bound-component");

const DcHostMixin = {
  inject: {
    hostDynamicComponentId: {
      from: "hostDynamicComponentId",
      default: null,
    },
    renderingInProduction: {
      from: "renderingInProduction",
      default: false,
    },
  },
  // XXX: Every DC host answers the one question here, so the eleven hosts get
  // XXX: validated by declaring this ONCE. Pure: no Vue instance, no store, so
  // XXX: the builder and a compile pass can both call it.
  configurationProblems({ element, dynamicComponent }) {
    const stored = element?.getRawOptionValue?.("referenced-component-id", "default", "default")?.r;
    if (boundComponentIdFromStored(stored)) {
      return [];
    }
    return [unboundComponentMessage({
      label: element?.displayName
        || element?.elementDef?.elementPanelSettings?.displayName
        || element?.type
        || "element",
      elementId: element?.id ?? "unknown",
      dynamicComponentId: dynamicComponent?.id ?? "unknown",
    })];
  },

  computed: {
    destinationComponentItems() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId || !isBuilderAppBooted()) {
        return [];
      }
      return App.get(PLATFORM_ENTITY_APP_NAME)
          .getFeedItems("Destination", { id: destinationId }, "components") ?? [];
    },
    boundComponentId() {
      const stored = Number(this.getOptionValue?.(ReferencedComponentId));
      return Number.isFinite(stored) && stored > 0 ? stored : 0;
    },
    resolvedHostComponentId() {
      return boundComponentIdFromStored(this.boundComponentId);
    },
    // XXX: A DC host with no stored component id is BROKEN DATA, not a benign
    // XXX: empty state. This used to return null and every host template's v-if
    // XXX: silently rendered nothing -- which is how the page footer shipped to
    // XXX: staging as an empty 11-character div with no error anywhere.
    // XXX: On a compiled surface it THROWS and the boundary in Staging.vue /
    // XXX: Production.vue turns that into the error page. Do NOT add a flag to
    // XXX: downgrade this to a skip -- fix the binding instead.
    embeddedDcId() {
      const resolved = this.resolvedHostComponentId;
      if (resolved) {
        return resolved;
      }
      if (isCompiledRenderSurface()) {
        throw new DynamicComponentError(this.unboundHostComponentMessage, {
          kind: "unbound-host",
          elementIdentifier: this.identifier,
          elementType: this.$options?.name,
          dynamicComponentId: this.dynamicComponentInstance?.id ?? this.hostDynamicComponentId,
          storedComponentId: this.boundComponentId,
          defaultComponentName: this.defaultComponentName,
        });
      }
      // eslint-disable-next-line no-console
      console.error(this.unboundHostComponentMessage);
      return null;
    },
    unboundHostComponentMessage() {
      return unboundComponentMessage({
        label: this.$options?.elementPanelSettings?.displayName ?? this.$options?.name,
        elementId: this.identifier,
        dynamicComponentId: this.dynamicComponentInstance?.id ?? this.hostDynamicComponentId,
      });
    },
    elementMisconfiguredReason() {
      return this.resolvedHostComponentId ? "" : this.unboundHostComponentMessage;
    },
    hostWrapperStyle() {
      const style = {};
      const setIf = (key, value, cls) => {
        if (value === undefined || value === null || value === "" || value === "unset") {
          return;
        }
        if (cls && typeof cls.getCssValue === "function") {
          style[key] = cls.getCssValue(value);
          return;
        }
        style[key] = typeof value === "number" ? `${value}px` : value;
      };
      setIf("width", this.getOptionValue?.(LayoutWidth), LayoutWidth);
      setIf("height", this.getOptionValue?.(LayoutHeight), LayoutHeight);
      setIf("max-width", this.getOptionValue?.(MaxWidth), MaxWidth);
      setIf("max-height", this.getOptionValue?.(MaxHeight), MaxHeight);
      setIf("min-width", this.getOptionValue?.(MinWidth), MinWidth);
      setIf("min-height", this.getOptionValue?.(MinHeight), MinHeight);
      return style;
    },
  },
  methods: {
    // XXX: PARAMETERISED, so it is a METHOD. It used to sit in computed while
    // XXX: being invoked as a function: Vue evaluated it once with target
    // XXX: undefined, and the call then coerced a component proxy into a property
    // XXX: key -- "Cannot convert object to primitive value". That one misplacement
    // XXX: is why EVERY DC host silently failed to resolve and the page footer
    // XXX: rendered an empty div on staging.
    componentIdByNameOnDestination(target) {
      if (!target) {
        return 0;
      }
      if (!isBuilderAppBooted()) {
        const mapped = window.blclient?.data?.destinationComponents?.[target];
        return mapped ? Number(mapped) : 0;
      }
      const app = App.get(PLATFORM_ENTITY_APP_NAME);
      const selfId = Number(this.dynamicComponentInstance?.id);
      for (const item of this.destinationComponentItems) {
        const id = item?.entityPk?.id;
        if (!id) {
          continue;
        }
        if (Number(id) === selfId) {
          continue;
        }
        if (app.getEntityField("DynamicComponent", { id: String(id) }, "status") === "inactive") {
          continue;
        }
        const name = app.getEntityField(
          "DynamicComponent",
          { id: String(id) },
          "name",
        );
        if (name === target) {
          return Number(id);
        }
      }
      return 0;
    },
  },
};

export default DcHostMixin;
